import { Component, OnInit } from '@angular/core';
import { SessionService } from '../../../../../app/core/services/session.service';
import { AdministratorService } from '@app/core/services/administrator.service';
import { NotificationService } from '@app/core/services/notification.service';
import { Administrator } from '@app/core/types/administrator';
import { TranslateService } from '@ngx-translate/core';
import { DateFormat } from '@app/core/types/date-format';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { filter, mergeMap } from 'rxjs';
import { FormBuilder, FormGroupDirective, UntypedFormGroup, Validators } from '@angular/forms';
import { CompareValidation } from '@app/core/validation/comparison-validation';
import { PasswordValidation } from '@app/core/validation/password-validation';

@Component({
    selector: 'itfg-administrator-profile',
    templateUrl: './administrator-profile.component.html',
    styleUrls: ['./administrator-profile.component.scss'],
    standalone: false
})
export class AdministratorProfileComponent implements OnInit {
  operator: Administrator = {};
  columns = [];
  fromNowDateFormat = DateFormat.FROM_NOW;
  updatePasswordForm!: UntypedFormGroup;
  updatePasswordErrored: boolean = false
  updatePasswordErrorMessage = '';
  passwordRequirements = [
    { errorKey: 'missingUpperCase', labelKey: 'auth.passwordChecks.upperCase' },
    { errorKey: 'missingLowerCase', labelKey: 'auth.passwordChecks.lowerCase' },
    { errorKey: 'missingDigit', labelKey: 'auth.passwordChecks.digit' },
    {
      errorKey: 'missingSpecialChar',
      labelKey: 'auth.passwordChecks.specialChar',
    },
  ];

  constructor(
    private sessionService: SessionService,
    private notification: NotificationService,
    private translateService: TranslateService,
    private _dialogService: DialogProviderService,
    private formBuilder: FormBuilder,
    private notifications: NotificationService
  ) { }

  ngOnInit() {
    this.operator = this.sessionService.currentlyLoggedOperator;
    this.sessionService.administratorLoaded.subscribe(user => {
      this.operator = user;
    });
    this.initializeColumns();
    this.updatePasswordForm = this.createUpdatePasswordForm();
  }

  createUpdatePasswordForm() {
    return this.formBuilder.group({
      oldPassword: ['', [Validators.required]],
      password: [
        '',
        [
          Validators.required,
          PasswordValidation.containsUpperCase,
          PasswordValidation.containsLowerCase,
          PasswordValidation.containsDigit,
          PasswordValidation.containsSpecialChar,
        ],
      ],
      passwordConfirm: ['', [Validators.required]],
    }, {
      validators: CompareValidation.fieldsAreEqualV2('passwordsNotEqual', [
        'password',
        'passwordConfirm',
      ]),
    });
  }

  initializeColumns() {
    this.translateService
      .get([
        'twoFactorAuth.devices.expire',
        'twoFactorAuth.devices.createTime',
        'twoFactorAuth.devices.lastUsed',
        'twoFactorAuth.devices.browser',
        'global.actions',
      ])
      .subscribe(translations => {
        this.columns = [
          {
            name: 'userAgent.agent',
            label: translations['twoFactorAuth.devices.browser'],
            width: { max: 200 },
          },
          {
            name: 'createTime',
            label: translations['twoFactorAuth.devices.createTime'],
          },
          {
            name: 'lastUsed',
            label: translations['twoFactorAuth.devices.lastUsed'],
          },
          {
            name: 'expire',
            label: translations['twoFactorAuth.devices.expire'],
          },
          {
            name: 'actions',
            label: translations['global.actions'],
          },
        ];
      });
  }

  revokeTrustedDeviceById(deviceId: number) {
    this._dialogService
      .openConfirm({
        message: this.translateService.instant('twoFactorAuth.revokeTrustedDevicePrompt'),
        disableClose: false,
      })
      .afterClosed()
      .pipe(
        filter(accept => accept === true),
        mergeMap(() => this.sessionService.revokeTrustedDeviceById(deviceId)),
        mergeMap(() => this.sessionService.refreshCurrentlyLoggedOperator$())
      )
      .subscribe(() => {
        this.notification.showLocalizedSuccessMessage({
          notificationText: 'twoFactorAuth.successfullyRevokedTrustedDevice',
        });
      });
  }

  revokeTrustedDevicesAccess() {
    this._dialogService
      .openConfirm({
        message: this.translateService.instant('twoFactorAuth.revokeTrustedDevicesPrompt'),
        disableClose: false,
      })
      .afterClosed()
      .pipe(
        filter(accept => accept === true),
        mergeMap(() => this.sessionService.revokeTrustedDevices()),
        mergeMap(() => this.sessionService.refreshCurrentlyLoggedOperator$())
      )
      .subscribe(() => {
        this.notification.showLocalizedSuccessMessage({
          notificationText: 'twoFactorAuth.successfullyRevokedTrustedDevice',
        });
      });
  }

  onSubmit(formDirective: FormGroupDirective): void {
    this.updatePasswordErrored = false;
    if (this.updatePasswordForm.valid) {
      const oldPassword = this.updatePasswordForm.value['oldPassword'];
      const newPassword = this.updatePasswordForm.value['password'];
      this.sessionService.updatePassword(oldPassword, newPassword)
        .pipe(
          mergeMap(() => this.sessionService.refreshCurrentlyLoggedOperator$()),
        )
        .subscribe({
          next: (res: Administrator) => {
            this.notifications.showLocalizedSuccessMessage({
              notificationText: 'auth.updatePasswordSuccessMessage'
            });
            this.updatePasswordForm.reset();
            formDirective.resetForm();
          },
          error: (err) => {
            this.updatePasswordErrored = true;
            this.updatePasswordErrorMessage = this.translateService.instant("auth.passwordValidation.errorCodes." + err.error[0].code);
          }
        });
    }
  }

}